// src/utils/cache.ts

import type { OverpassResponse } from './overpass';

/** Clave única para el caché según centro y radio */
export function getCacheKey(lat: number, lng: number, radius: number): string {
  return `city_cache_${lat.toFixed(6)}_${lng.toFixed(6)}_${radius}`;
}

/** Entrada de caché con timestamp para expirar datos antiguos */
interface CachedEntry {
  data: OverpassResponse;
  timestamp: number; // Date.now() al guardar
}

const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 días

/** Lee la respuesta cruda desde localStorage, expirando entradas viejas */
export function loadRawFromCache(key: string): OverpassResponse | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CachedEntry;
    // Entradas corruptas o del formato plano anterior (sin TTL): limpiar y re-descargar
    const isLegacy = !entry || typeof entry.timestamp !== 'number' || !entry.data;
    if (isLegacy || Date.now() - entry.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

/** Guarda la respuesta cruda en localStorage con su timestamp */
export function saveRawToCache(key: string, data: OverpassResponse): void {
  try {
    const entry: CachedEntry = { data, timestamp: Date.now() };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Sin espacio o modo privado: ignorar silenciosamente
  }
}

/** Elimina todas las entradas de caché de la ciudad */
export function clearCityCache(): void {
  try {
    const keys = Object.keys(localStorage).filter((k) =>
      k.startsWith('city_cache_')
    );
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignorar
  }
}